import { v4 as randomUUID } from 'uuid';
import { MediaType, ScanEventPhotoType } from '../../../../../../backend/src/types.generated';
import { BAG_AUDIT_CONSTANTS, BAG_AUDIT_ERRORS } from './constants';
import type { BagAuditError, BagAuditObject, PhotoRecord, PhotoUploadResult } from './types';

interface ScanEventPhotoInput {
	scanEvent: { id: string };
	photo: { filename: string; type: MediaType };
	createdAt: string;
	createdByApplicationType: string;
	createdByUser: { id: string };
	photoType: ScanEventPhotoType;
}

/**
 * Service class for handling bag audit photo operations
 */
export class BagAuditPhotoService {
	constructor(
		private getUploadUrlMutation: (variables: {
			key: string;
		}) => Promise<{ data?: { getUploadUrl?: { url: string; filename: string } } }>,
		private createPhotoMutation: (variables: {
			variables: { input: ScanEventPhotoInput };
		}) => Promise<{ data?: { createScanEventPhoto?: { id: string } } }>
	) {}

	/**
	 * Uploads a single photo to S3
	 */
	async uploadPhotoToS3(photo: string, type: ScanEventPhotoType): Promise<PhotoUploadResult> {
		try {
			// Generate unique S3 key
			const s3Key = `${BAG_AUDIT_CONSTANTS.S3_PATH_PREFIX}/${randomUUID()}.jpg`;

			// Get signed upload URL
			const { data: uploadUrlData } = await this.getUploadUrlMutation({ key: s3Key });

			if (!uploadUrlData?.getUploadUrl) {
				throw new Error(BAG_AUDIT_ERRORS.UPLOAD_URL_FAILED);
			}

			// Convert photo to bytes and upload
			const photoBytes = await fetch(photo).then((res) => res.arrayBuffer());
			const response = await fetch(uploadUrlData.getUploadUrl.url, {
				method: 'PUT',
				body: photoBytes,
				headers: {
					'Content-Type': BAG_AUDIT_CONSTANTS.IMAGE_CONTENT_TYPE,
				},
			});

			if (!response.ok) {
				throw new Error(BAG_AUDIT_ERRORS.PHOTO_UPLOAD_FAILED);
			}

			return {
				filename: uploadUrlData.getUploadUrl.filename,
				type: type,
			};
		} catch {
			throw new Error(BAG_AUDIT_ERRORS.PHOTO_UPLOAD_FAILED);
		}
	}

	/**
	 * Creates a database record for an uploaded photo
	 */
	async createPhotoRecord(
		filename: string,
		type: ScanEventPhotoType,
		scanEventId: string,
		userId: string
	): Promise<PhotoRecord> {
		try {
			const { data: photoData } = await this.createPhotoMutation({
				variables: {
					input: {
						scanEvent: { id: scanEventId },
						photo: { filename, type: MediaType.Image },
						createdAt: new Date().toISOString(),
						createdByApplicationType: BAG_AUDIT_CONSTANTS.APPLICATION_TYPE,
						createdByUser: { id: userId },
						photoType: type,
					},
				},
			});

			if (!photoData?.createScanEventPhoto) {
				throw new Error(BAG_AUDIT_ERRORS.PHOTO_RECORD_FAILED);
			}

			return photoData.createScanEventPhoto;
		} catch {
			throw new Error(BAG_AUDIT_ERRORS.PHOTO_RECORD_FAILED);
		}
	}

	/**
	 * Handles the complete photo upload and record creation process
	 */
	async processPhotos(
		photos: string[],
		scanEventId: string,
		userId: string
	): Promise<{ success: boolean; error?: BagAuditError }> {
		if (photos.length === 0) {
			return { success: true };
		}

		try {
			// Upload all photos to S3 in parallel
			const uploadPromises = photos.map((photo) =>
				this.uploadPhotoToS3(photo, BAG_AUDIT_CONSTANTS.PHOTO_TYPE as ScanEventPhotoType)
			);

			const uploadResults = await Promise.allSettled(uploadPromises);

			// Check for failed uploads
			const failedUploads = uploadResults.filter((result) => result.status === 'rejected');
			if (failedUploads.length > 0) {
				return { success: false, error: BAG_AUDIT_ERRORS.PHOTO_UPLOAD_FAILED };
			}

			// Collect successful uploads
			const photoUploads: PhotoUploadResult[] = [];
			uploadResults.forEach((result) => {
				if (result.status === 'fulfilled') {
					photoUploads.push(result.value);
				}
			});

			// Create all database records in parallel
			const dbPromises = photoUploads.map(({ filename, type }) =>
				this.createPhotoRecord(filename, type as ScanEventPhotoType, scanEventId, userId)
			);

			const dbResults = await Promise.allSettled(dbPromises);

			// Check for failed database creations
			const failedDbCreations = dbResults.filter((result) => result.status === 'rejected');
			if (failedDbCreations.length > 0) {
				// Photos are already uploaded to S3, so this is a partial failure
				return { success: false, error: BAG_AUDIT_ERRORS.PHOTO_RECORD_FAILED };
			}

			return { success: true };
		} catch {
			return { success: false, error: BAG_AUDIT_ERRORS.PHOTO_UPLOAD_FAILED };
		}
	}
}

interface FacilityBagAuditInput {
	scannableCode: string;
	notes?: string;
	contaminationAmount: number;
	isContaminated: boolean;
	isDamaged: boolean;
	facilityId: string;
	weight: number;
}

/**
 * Service class for handling bag audit operations
 */
export class BagAuditService {
	constructor(
		private facilityBagAuditMutation: (variables: {
			variables: { input: FacilityBagAuditInput };
		}) => Promise<{ data?: { facilityBagAudit?: { id: string } } }>,
		private photoService: BagAuditPhotoService
	) {}

	/**
	 * Creates a bag audit record and processes associated photos
	 */
	async submitBagAudit(
		bagData: BagAuditObject,
		facilityId: string,
		userId: string
	): Promise<{ success: boolean; error?: BagAuditError }> {
		try {
			// Step 1: Create the bag audit record
			const { data } = await this.facilityBagAuditMutation({
				variables: {
					input: {
						scannableCode: bagData.scannableCode,
						notes: bagData.contaminationDetails,
						contaminationAmount: bagData.contamination,
						weight: bagData.weight || 0,
						isContaminated: bagData.isContaminated,
						isDamaged: bagData.isDamaged,
						facilityId,
					},
				},
			});

			if (!data?.facilityBagAudit?.id) {
				return { success: false, error: BAG_AUDIT_ERRORS.AUDIT_CREATION_FAILED };
			}

			const scanEventId = data.facilityBagAudit.id;

			// Step 2: Process photos if any exist
			const photoResult = await this.photoService.processPhotos(
				bagData.photos,
				scanEventId,
				userId
			);

			if (!photoResult.success) {
				return photoResult;
			}

			return { success: true };
		} catch {
			return { success: false, error: BAG_AUDIT_ERRORS.AUDIT_CREATION_FAILED };
		}
	}
}
